import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

/**
 * This context persists document explorer state (expanded folders, active node, selected files)
 * at the MatterLayout level so it survives navigation between tabs within the same matter.
 */

export interface DocumentExplorerPersistedState {
  expandedFolders: Set<string>;
  activeNodeId: string;
  selectedFileIds: Set<string>;
  selectedFolderIds: Set<string>;
}

interface DocumentExplorerStateContextType {
  state: DocumentExplorerPersistedState;
  setState: (state: Partial<DocumentExplorerPersistedState>) => void;
  setExpandedFolders: (folders: Set<string>) => void;
  setActiveNodeId: (id: string) => void;
  setSelectedFileIds: (ids: Set<string>) => void;
  setSelectedFolderIds: (ids: Set<string>) => void;
  hasInitialized: boolean;
  setHasInitialized: (value: boolean) => void;
}

const DocumentExplorerStateContext = createContext<
  DocumentExplorerStateContextType | undefined
>(undefined);

interface DocumentExplorerStateProviderProps {
  children: React.ReactNode;
}

export const DocumentExplorerStateProvider = ({
  children,
}: DocumentExplorerStateProviderProps) => {
  const [expandedFolders, setExpandedFoldersInternal] = useState<Set<string>>(
    new Set(),
  );
  const [activeNodeId, setActiveNodeIdInternal] = useState<string>('');
  const [selectedFileIds, setSelectedFileIdsInternal] = useState<Set<string>>(
    new Set(),
  );
  const [selectedFolderIds, setSelectedFolderIdsInternal] = useState<
    Set<string>
  >(new Set());
  const [hasInitialized, setHasInitialized] = useState(false);

  const setExpandedFolders = useCallback((folders: Set<string>) => {
    setExpandedFoldersInternal(folders);
  }, []);

  const setActiveNodeId = useCallback((id: string) => {
    setActiveNodeIdInternal(id);
  }, []);

  const setSelectedFileIds = useCallback((ids: Set<string>) => {
    setSelectedFileIdsInternal(ids);
  }, []);

  const setSelectedFolderIds = useCallback((ids: Set<string>) => {
    setSelectedFolderIdsInternal(ids);
  }, []);

  const setState = useCallback(
    (newState: Partial<DocumentExplorerPersistedState>) => {
      if (newState.expandedFolders !== undefined) {
        setExpandedFoldersInternal(newState.expandedFolders);
      }
      if (newState.activeNodeId !== undefined) {
        setActiveNodeIdInternal(newState.activeNodeId);
      }
      if (newState.selectedFileIds !== undefined) {
        setSelectedFileIdsInternal(newState.selectedFileIds);
      }
      if (newState.selectedFolderIds !== undefined) {
        setSelectedFolderIdsInternal(newState.selectedFolderIds);
      }
    },
    [],
  );

  const state = useMemo(
    () => ({
      expandedFolders,
      activeNodeId,
      selectedFileIds,
      selectedFolderIds,
    }),
    [expandedFolders, activeNodeId, selectedFileIds, selectedFolderIds],
  );

  const value = useMemo(
    () => ({
      state,
      setState,
      setExpandedFolders,
      setActiveNodeId,
      setSelectedFileIds,
      setSelectedFolderIds,
      hasInitialized,
      setHasInitialized,
    }),
    [
      state,
      setState,
      setExpandedFolders,
      setActiveNodeId,
      setSelectedFileIds,
      setSelectedFolderIds,
      hasInitialized,
      setHasInitialized,
    ],
  );

  return (
    <DocumentExplorerStateContext.Provider value={value}>
      {children}
    </DocumentExplorerStateContext.Provider>
  );
};

export const useDocumentExplorerState = ():
  | DocumentExplorerStateContextType
  | undefined => {
  return useContext(DocumentExplorerStateContext);
};
